import { useCallback, useEffect, useState } from "react";
import {
  getMenuBadges,
  setMenuBadge,
  resetMenuBadges,
  subscribeMenuBadges,
} from "../utilities/menuBadges";
import {
  apiFetchInboxConversationCounts,
  apiFetchBrandOwnerStats,
  apiFetchPartnerCod,
  apiGetDashboardStats,
  apiFetchPartnerDashboard,
} from "../api/index.js";
import { getActiveCountry } from "../utilities/activeCountry.js";

/**
 * Feeds utilities/menuBadges from the live sources the dashboard ALREADY has.
 *
 * DESIGN RULE: no badge invents a data source, and no badge adds a poll to an
 * endpoint that cannot afford one. Every count below is either pushed over a
 * socket the app is already connected to, or read from an endpoint that does
 * `countDocuments` rather than loading rows. Two counts that would have been
 * useful are deliberately absent for that reason, and are noted at the bottom.
 *
 * There is one SYNC hook, mounted once beside the other navbar syncs, and a
 * separate reader hook for the Sidebar. Mounting the sync twice would double
 * every request; the reader can be used anywhere.
 */

const REFRESH_MS = 3 * 60 * 1000;

/** Read the badges. Safe to call from any component. */
export function useMenuBadges() {
  const [badges, setBadges] = useState(getMenuBadges);
  useEffect(() => subscribeMenuBadges(setBadges), []);
  return badges;
}

/**
 * Populate the badges for whoever is signed in.
 *
 * @param {object}  user          the profile shape from getUserFromLocalStorage
 * @param {object}  socket        the navbar's existing socket, or null
 * @param {boolean} isLusukuStaff platform staff, as the navbar already derives it
 */
export function useMenuBadgeSync({ user, socket, isLusukuStaff, withInbox = true } = {}) {
  const result = user?.result;
  const partnerId = result?.partnerId || null;
  const operationMode = result?.operationMode || null;
  const is3PL = operationMode === "3PL_PARTNER" || operationMode === "3PL_STAFF";
  const isBrandOwner = operationMode === "BRAND_OWNER";
  // The inbox is keyed on the vendor a user acts for, exactly as AdminNavbar
  // resolves it for the same endpoint.
  // A member limited to some branches has no inbox (head office only, 4 Oct
  // 2026), so there is no count to ask for.
  const inboxVendorId = withInbox ? result?.parentVendorId || result?._id || result?.id || null : null;

  const refresh = useCallback(async () => {
    if (!result) return;

    // Unanswered conversations. Everyone with an inbox has this one.
    if (inboxVendorId) {
      try {
        const { data } = await apiFetchInboxConversationCounts(inboxVendorId);
        setMenuBadge("inboxUnread", data?.counts?.Unread || 0);
      } catch (_e) {
        /* keep the last known number; a failed count must never blank a badge */
      }
    }

    // Orders still waiting to be processed, platform-wide.
    if (isLusukuStaff) {
      try {
        const { data } = await apiGetDashboardStats();
        setMenuBadge("adminProcessingOrders", data?.processingCount || 0);
        setMenuBadge("ticketOrdersAttention", data?.ticketAttentionCount || 0);
      } catch (_e) {
        /* ignore */
      }
    }

    if (isBrandOwner) {
      try {
        const { data } = await apiFetchBrandOwnerStats();
        setMenuBadge("brandOpenQuotes", data?.data?.openQuoteRequests || 0);
      } catch (_e) {
        /* ignore */
      }
    }

    if (is3PL && partnerId) {
      try {
        // Riders blocked by held cash. This is the badge that explains why a
        // partner's riders went quiet, which was previously invisible.
        const { data } = await apiFetchPartnerCod(partnerId);
        setMenuBadge("partnerFlaggedRiders", data?.data?.totals?.flaggedRiders || 0);
      } catch (_e) {
        /* a staff member without riders:view gets a 403 here, which is correct */
      }
      try {
        // Orders handed to this partner and still waiting for a rider.
        //
        // AdminNavbar keeps a similar number for its bell, but by adding and
        // subtracting one per socket frame, and it zeroes on bell-dismiss. A
        // menu badge must not work that way: dismissing a notification does
        // not deliver the order, and a tab that missed a frame while
        // backgrounded would drift for the rest of the session. Re-asking the
        // server is slower and always right.
        const { data } = await apiFetchPartnerDashboard(partnerId);
        setMenuBadge("partnerNewAssigned", data?.data?.orders?.newAssigned || 0);
      } catch (_e) {
        /* ignore */
      }
    }
  }, [result, inboxVendorId, isLusukuStaff, isBrandOwner, is3PL, partnerId]);

  // Snapshot on mount, when the tab comes back, and on a slow timer. The timer
  // is the backstop; the socket frames below are what make it feel live.
  useEffect(() => {
    if (!result) {
      resetMenuBadges();
      return undefined;
    }
    refresh();
    const onFocus = () => refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    const id = setInterval(refresh, REFRESH_MS);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
      clearInterval(id);
    };
  }, [result, refresh]);

  // Get INTO the room the count is broadcast to. The server pushes
  // `dashboardUpdate` (with processingCount) to `dashboardRoom:<country>`
  // within ~5s of any order write, but only sockets that emitted
  // `joinDashboard` are in that room, and until now the only one that did was
  // the home Dashboard view, on a socket of its own. On every other page the
  // navbar socket sat in its vendor room, so "N new orders need you" learned
  // about a cancelled or processed order from the three minute poll or a
  // reload. Re-joining on every (re)connect is enough to follow a country
  // switch, because that switch hard-reloads the page
  // (AdminNavbar.handleCountrySwitch) and the socket comes back fresh.
  useEffect(() => {
    if (!socket || !result || !isLusukuStaff) return undefined;
    const userId = result._id || result.id;
    const joinDashboardRoom = () =>
      socket.emit("joinDashboard", userId, getActiveCountry());
    if (socket.connected) joinDashboardRoom();
    socket.on("connect", joinDashboardRoom);
    return () => socket.off("connect", joinDashboardRoom);
  }, [socket, result, isLusukuStaff]);

  // Live corrections. Each of these events is one the dashboard is already
  // subscribed to elsewhere, so this adds listeners, not connections.
  useEffect(() => {
    if (!socket || !result) return undefined;

    // The platform dashboard pushes its whole stats object; take the one field
    // rather than re-fetching, since the server has already counted.
    const onDashboard = (payload) => {
      if (!isLusukuStaff) return;
      const n = payload?.processingCount;
      if (typeof n === "number") setMenuBadge("adminProcessingOrders", n);
      const ticketsWaiting = payload?.ticketAttentionCount;
      if (typeof ticketsWaiting === "number") setMenuBadge("ticketOrdersAttention", ticketsWaiting);
    };

    // Everything else only tells us that SOMETHING changed, not the new total.
    // Re-ask rather than doing client-side arithmetic: a tab that missed a
    // frame while backgrounded would drift for the rest of the session, and a
    // count that is quietly wrong is worse than one that is a second late.
    let t = null;
    const recount = () => {
      clearTimeout(t);
      t = setTimeout(refresh, 1200);
    };

    socket.on("dashboardUpdate", onDashboard);
    const recountEvents = [
      "connect",
      "newOrderAlert",
      "newOrder",
      "newRestaurantOrder",
      "newHealthcareOrder",
      "newBrandOrder",
      "order:assignedTo3PL",
      "order:recalledFrom3PL",
      "order:status_changed",
      "order:accepted",
      "brandQuote:newRequest",
      "inbox:message_received",
      "inbox:message_sent",
      "inbox:conversation_assigned",
      "inbox:conversation_closed",
      "rider:flagged",
    ];
    recountEvents.forEach((ev) => socket.on(ev, recount));

    return () => {
      clearTimeout(t);
      socket.off("dashboardUpdate", onDashboard);
      recountEvents.forEach((ev) => socket.off(ev, recount));
    };
  }, [socket, result, isLusukuStaff, refresh]);
}

/*
 * DELIBERATELY NOT BADGED, and why:
 *
 * - Vendor "new orders" for restaurants and retailers. The stats routes that
 *   carry the number (/weborders/restaurant-vendor/:id/stats and
 *   /weborders/stats-seller/:id) currently have NO auth middleware, and the
 *   restaurant one loads every order for the vendor and counts them in JS.
 *   Pointing a three minute poll at those would be both a leak and a load
 *   problem. Harden and make them countDocuments first, then add the key.
 * - Riders online. It is ambient status, not work waiting: it is healthy when
 *   it is large and never reaches zero, so as a badge it would be permanent
 *   furniture and would teach people to stop reading the badges that matter.
 * - Deletion requests, pending payouts, low stock, offline terminals. Each
 *   would be a good nudge and none has a count endpoint today; they return
 *   whole lists. They need a count route before a badge can be honest.
 */
